import React from 'react';
import {
  Box,
  Typography,
  TextField,
  Divider,
  Button,
  CircularProgress,
} from '@mui/material';
import { Check as CheckIcon } from '@mui/icons-material';
import { Customer } from '../../../types';

interface FormData {
  name: string;
  operations_account_id: string;
  operations_account_role: string;
  operations_account_write_role: string;
  operations_account_automation_role: string;
  operations_account_dataset_role: string;
  operations_account_external_id: string;
  operations_account_region: string;
  operations_event_bus_name: string;
  knowledge_base_id: string;
  knowledge_base_bucket: string;
  portal_url: string;
}

interface CustomerFormProps {
  formData: FormData;
  selectedCustomer: Customer | null;
  isNewCustomer: boolean;
  fieldErrors: Record<string, string | null>;
  testing: boolean;
  onFieldChange: (field: keyof FormData, value: string) => void;
  onTestRole: (roleType: 'readonly' | 'write') => void;
}

const CustomerForm: React.FC<CustomerFormProps> = ({
  formData,
  selectedCustomer,
  isNewCustomer,
  fieldErrors,
  testing,
  onFieldChange,
  onTestRole,
}) => (
  <Box sx={{ display: 'grid', gap: 3 }}>
    {!isNewCustomer && selectedCustomer && (
      <TextField
        fullWidth
        label="Customer ID"
        value={selectedCustomer.id}
        disabled
        InputLabelProps={{ shrink: true }}
        helperText="Unique identifier for this customer"
      />
    )}

    <TextField
      label="Customer Name *"
      value={formData.name}
      onChange={(e) => onFieldChange('name', e.target.value)}
      placeholder="Acme Corporation"
      helperText="Friendly name for this customer"
      InputLabelProps={{ shrink: true }}
      fullWidth
    />

    <TextField
      fullWidth
      label="Operations AWS Account ID *"
      value={formData.operations_account_id}
      onChange={(e) => {
        const value = e.target.value.replace(/\D/g, '');
        onFieldChange('operations_account_id', value);
      }}
      placeholder="123456789012"
      inputProps={{
        maxLength: 12,
        pattern: '[0-9]{12}',
        inputMode: 'numeric'
      }}
      error={!!fieldErrors.operations_account_id}
      helperText={fieldErrors.operations_account_id || "12-digit AWS account ID for customer operations account"}
      InputLabelProps={{ shrink: true }}
    />

    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
      <TextField
        fullWidth
        label="Read-Only Role Name"
        value={formData.operations_account_role}
        onChange={(e) => onFieldChange('operations_account_role', e.target.value)}
        error={!!fieldErrors.operations_account_role}
        helperText={fieldErrors.operations_account_role || "IAM role for L1 read-only access (investigations, viewing)"}
        placeholder="e.g., cloud2-ops-readonly"
        InputLabelProps={{ shrink: true }}
      />
      {!isNewCustomer && selectedCustomer && formData.operations_account_role && (
        <Button
          variant="contained"
          startIcon={testing ? <CircularProgress size={16} /> : <CheckIcon />}
          onClick={() => onTestRole('readonly')}
          disabled={testing}
          sx={{ mt: 1, whiteSpace: 'nowrap' }}
        >
          Test
        </Button>
      )}
    </Box>

    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
      <TextField
        fullWidth
        label="Write Role Name"
        value={formData.operations_account_write_role}
        onChange={(e) => onFieldChange('operations_account_write_role', e.target.value)}
        error={!!fieldErrors.operations_account_write_role}
        helperText={fieldErrors.operations_account_write_role || "IAM role for L2 write access (remediation, modifications)"}
        placeholder="e.g., cloud2-ops-admin"
        InputLabelProps={{ shrink: true }}
      />
      {!isNewCustomer && selectedCustomer && formData.operations_account_write_role && (
        <Button
          variant="contained"
          startIcon={testing ? <CircularProgress size={16} /> : <CheckIcon />}
          onClick={() => onTestRole('write')}
          disabled={testing}
          sx={{ mt: 1, whiteSpace: 'nowrap' }}
        >
          Test
        </Button>
      )}
    </Box>

    <TextField
      fullWidth
      label="Automation Role Name"
      value={formData.operations_account_automation_role}
      onChange={(e) => onFieldChange('operations_account_automation_role', e.target.value)}
      error={!!fieldErrors.operations_account_automation_role}
      helperText={fieldErrors.operations_account_automation_role || "IAM role for automation (Lambda invoke, SSM, Service Catalog)"}
      placeholder="e.g., cloud2-ops-automation"
      InputLabelProps={{ shrink: true }}
    />

    <TextField
      fullWidth
      label="Dataset curator role"
      value={formData.operations_account_dataset_role}
      onChange={(e) => onFieldChange('operations_account_dataset_role', e.target.value)}
      error={!!fieldErrors.operations_account_dataset_role}
      helperText={fieldErrors.operations_account_dataset_role || "IAM role that curates Event Datasets in the operations account (Glue, Athena, S3)"}
      placeholder="e.g., cloud2-event-datasets-curator"
      InputLabelProps={{ shrink: true }}
    />

    <TextField
      fullWidth
      label="Operations Account External ID"
      value={formData.operations_account_external_id}
      onChange={(e) => onFieldChange('operations_account_external_id', e.target.value)}
      placeholder="shared-secret-external-id"
      helperText="External ID for enhanced security when assuming the cross-account role (optional but recommended)"
      InputLabelProps={{ shrink: true }}
    />

    <TextField
      fullWidth
      label="Operations Account Region"
      value={formData.operations_account_region}
      disabled
      helperText="Every operations account runs in this region"
      InputLabelProps={{ shrink: true }}
    />

    <Divider sx={{ my: 2 }} />
    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
      Knowledge Base (Optional)
    </Typography>

    <TextField
      fullWidth
      label="Knowledge Base ID"
      value={formData.knowledge_base_id}
      onChange={(e) => onFieldChange('knowledge_base_id', e.target.value)}
      placeholder="e.g., ABCD1234XY"
      helperText="Bedrock KB ID, provisioned by LZA in the operations account (leave empty if not provisioned)"
      InputLabelProps={{ shrink: true }}
    />

    <TextField
      fullWidth
      label="Knowledge Base S3 Location"
      value={formData.knowledge_base_bucket}
      onChange={(e) => onFieldChange('knowledge_base_bucket', e.target.value)}
      placeholder="e.g., bucket-name or bucket-name/path/"
      helperText="S3 bucket name, optionally with path prefix (e.g., shared-bucket/customer-docs/)"
      InputLabelProps={{ shrink: true }}
    />

    <Divider sx={{ my: 2 }} />
    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
      Portal (Optional)
    </Typography>

    <TextField
      fullWidth
      label="Portal URL"
      value={formData.portal_url}
      onChange={(e) => onFieldChange('portal_url', e.target.value)}
      placeholder="https://app.cloud2.help"
      helperText="Customer portal login URL for invitation emails"
      InputLabelProps={{ shrink: true }}
    />
  </Box>
);

export default CustomerForm;
